import { useCallback, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Plus, Square } from 'lucide-react'
import { InviteMemberModal } from '../Room/InviteStudentModal'
import { TeamCreationForm } from './TeamCreationForm'
import { TeamsEmptyState } from './TeamsEmptyState'
import { TeamsDesktopTable } from './TeamsDesktopTable'
import { TeamsMobileView } from './TeamsMobileView'
import { useTeamManagement } from './useTeamManagement'
import { useAppSelector } from '@/redux/hooks'
import { GameRoomStatus } from '@/redux/types/room'

interface TeamsModalProps {
  isOpen: boolean
  onClose: () => void
  roomId: string
  dummyBotsRefreshKey?: number
}

export function TeamsModal({
  isOpen,
  onClose,
  roomId,
  dummyBotsRefreshKey = 0,
}: TeamsModalProps) {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

  const { currentRoom } = useAppSelector((state) => state.room)

  const isReadOnly = currentRoom?.status !== GameRoomStatus.NOT_STARTED

  const {
    teams,
    displayableAdvisors,
    players,
    handleAdvisorToggle,
    handlePlayerToggle,
    handleDeselectAll,
    isPlayerAssignedToTeam,
    isAdvisorAssignedToTeam,
    isDummyAssignedToTeam,
    addTeam,
    updateTeam,
    deleteTeam,
    saveTeams,
    getTableColumnStyles,
    getTeamMemberCount,
    hasTeamMembers,
    dummyBots,
    handleDummyToggle,
  } = useTeamManagement(roomId, isOpen, dummyBotsRefreshKey)

  const handleInviteNewPlayer = useCallback(() => {
    setIsInviteModalOpen(true)
  }, [])

  const handleInviteModalClose = useCallback(() => {
    setIsInviteModalOpen(false)
  }, [])

  const handleSaveTeams = useCallback(async () => {
    const success = await saveTeams()
    if (success) {
      onClose()
    }
  }, [saveTeams, onClose])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          aria-describedby={undefined}
          className='w-[95vw] max-w-7xl h-[90vh] p-0 flex flex-col'
        >
          <DialogHeader className='flex-shrink-0 px-6 py-4 border-b'>
            <DialogTitle>
              {isReadOnly ? 'Team Overview' : 'Team Management'}
            </DialogTitle>
            {isReadOnly && (
              <p className='mt-1 text-sm text-gray-600'>
                Teams cannot be modified after the game has started or ended.
              </p>
            )}
          </DialogHeader>

          {!isReadOnly && <TeamCreationForm onAddTeam={addTeam} />}

          <div className='overflow-auto flex-1 px-6 py-4 min-h-0'>
            {teams.length === 0 ? (
              <TeamsEmptyState />
            ) : (
              <>
                <TeamsDesktopTable
                  teams={teams}
                  advisors={displayableAdvisors}
                  players={players as any}
                  isAdvisorAssignedToTeam={isAdvisorAssignedToTeam}
                  isPlayerAssignedToTeam={isPlayerAssignedToTeam}
                  onAdvisorToggle={handleAdvisorToggle}
                  onPlayerToggle={handlePlayerToggle}
                  dummyBots={dummyBots}
                  isDummyAssignedToTeam={isDummyAssignedToTeam}
                  onDummyToggle={handleDummyToggle}
                  onUpdateTeam={updateTeam}
                  onDeleteTeam={deleteTeam}
                  getTeamMemberCount={getTeamMemberCount}
                  hasTeamMembers={hasTeamMembers}
                  tableColumnStyles={getTableColumnStyles()}
                  isReadOnly={isReadOnly}
                />

                <TeamsMobileView
                  teams={teams}
                  advisors={displayableAdvisors}
                  players={players as any}
                  isAdvisorAssignedToTeam={isAdvisorAssignedToTeam}
                  isPlayerAssignedToTeam={isPlayerAssignedToTeam}
                  onAdvisorToggle={handleAdvisorToggle}
                  onPlayerToggle={handlePlayerToggle}
                  dummyBots={dummyBots}
                  isDummyAssignedToTeam={isDummyAssignedToTeam}
                  onDummyToggle={handleDummyToggle}
                  onUpdateTeam={updateTeam}
                  onDeleteTeam={deleteTeam}
                  getTeamMemberCount={getTeamMemberCount}
                  hasTeamMembers={hasTeamMembers}
                  isReadOnly={isReadOnly}
                />
              </>
            )}
          </div>

          {teams.length > 0 && !isReadOnly && (
            <div className='flex-shrink-0 px-6 border-gray-200'>
              <div className='flex flex-col gap-3 sm:flex-row'>
                <Button
                  onClick={handleInviteNewPlayer}
                  className='w-full transition-colors bg-dark-blue hover:bg-dark-blue-hovered sm:w-auto'
                >
                  <Plus className='transition-transform group-hover:scale-110' />
                  Invite Player
                </Button>
                <Button
                  onClick={handleDeselectAll}
                  variant='outline'
                  className='w-full transition-colors sm:w-auto hover:bg-gray-100'
                >
                  <Square className='w-4 h-4' />
                  Deselect All
                </Button>
              </div>
            </div>
          )}

          <div className='flex-shrink-0 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-t'>
            <div className='flex flex-col gap-3 justify-end sm:flex-row'>
              <Button
                variant='outline'
                onClick={onClose}
                className='w-full transition-colors sm:w-auto hover:bg-gray-100'
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </Button>
              {!isReadOnly && (
                <Button
                  className='w-full transition-colors bg-dark-blue hover:bg-dark-blue-hovered sm:w-auto'
                  onClick={handleSaveTeams}
                >
                  Save Teams
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {!isReadOnly && (
        <InviteMemberModal
          canInviteProfessors={false}
          isOpen={isInviteModalOpen}
          onClose={handleInviteModalClose}
        />
      )}
    </>
  )
}
